import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {useState, FC, useEffect, useMemo} from 'react'
import {Row, Col, Pagination, Form, Spinner} from 'react-bootstrap'
import {ActivityLog} from '../core/_models'
import moment from 'moment'

type ActivityLogsTableProps = {
  rows?: ActivityLog[] | null
  isLoading: boolean
}

export const ActivityLogsTable: FC<ActivityLogsTableProps> = ({rows, isLoading}) => {
  const [listData, setListData] = useState<ActivityLog[]>([])
  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')

  useEffect(() => {
    setListData(rows || [])
  }, [rows])

  const columnHelper = createColumnHelper<ActivityLog>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'ps-4 text-start w-80px',
          cellClass: 'text-start w-80px',
        },
      }),
      columnHelper.accessor('table_name', {
        header: 'Table Name',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'ps-4 text-start w-150px',
          cellClass: 'text-start w-150px',
        },
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => info.getValue(),
        meta: {
          headerClass: 'ps-4 text-start',
          cellClass: 'text-start',
        },
      }),
      columnHelper.accessor('creator_name', {
        header: 'Creator',
        cell: (info) => info.getValue() || '-',
        meta: {
          headerClass: 'ps-4 text-start w-120px',
          cellClass: 'text-start w-120px',
        },
      }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => moment(info.getValue()).format('DD MMM YYYY, HH:mm:ss'),
        meta: {
          headerClass: 'ps-4 text-start w-180px',
          cellClass: 'text-start w-180px',
        },
      }),
    ],
    []
  )

  const dataTable = useReactTable({
    data: listData,
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    pageCount: Math.ceil((listData?.length || 0) / pagination.pageSize),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    const maxPagesToShow = 3
    const halfRange = Math.floor(maxPagesToShow / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    let endPage = Math.min(totalPages, currentPage + halfRange)

    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      }
    }

    const range: any = []

    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) {
        range.push('...')
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...')
      }
      range.push(totalPages)
    }

    return range
  }

  const handleNavigationPageChange = (nav: 'prev' | 'next') => {
    setPagination({
      ...pagination,
      pageIndex: nav === 'prev' ? Math.max(0, pagination.pageIndex - 1) : pagination.pageIndex + 1,
    })
  }

  const handleDisplayPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value,
    })
  }

  const pages = paginationRange()

  return (
    <div style={{minHeight: '400px', position: 'relative'}}>
      {isLoading ? (
        <div className='d-flex align-items-center justify-content-center' style={{height: '200px'}}>
          <Spinner animation='border' role='status' variant='primary' />
        </div>
      ) : (
        <>
          <Row className='mb-5 justify-content-between'>
            <Col lg={5} md={5} className='d-flex justify-content-start align-items-center'>
              Showing{' '}
              {dataTable.getRowModel().rows.length > 0
                ? pagination.pageIndex * pagination.pageSize + 1
                : 0}{' '}
              to {Math.min((pagination.pageIndex + 1) * pagination.pageSize, listData.length)} of{' '}
              {listData.length} results
            </Col>
            <Col lg={4} md={4}>
              <Form.Control
                type='text'
                placeholder='Search logs...'
                value={globalFilter}
                onChange={(e) => {
                  setPagination({
                    pageIndex: 0,
                    pageSize: pagination.pageSize,
                  })
                  setGlobalFilter(e.target.value)
                }}
              />
            </Col>
          </Row>
          <div className='table-responsive'>
            <table className='gy-3 table table-striped table-bordered table-hover align-middle'>
              <thead className='table-dark'>
                {dataTable.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} className='text-start'>
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className='ms-2'>
                              {{
                                asc: <i className='fa-solid fa-sort-up'></i>,
                                desc: <i className='fa-solid fa-sort-down'></i>,
                                false: <i className='fa-solid fa-sort'></i>,
                              }[header.column.getIsSorted() as string] ?? (
                                <i className='fa-solid fa-sort'></i>
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {dataTable.getRowModel().rows.length > 0 ? (
                  dataTable.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className='text-start'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className='align-middle'>
                    <td colSpan={99} className='text-center'>
                      No Data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                value={pagination.pageSize}
                onChange={handleDisplayPageChange}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
              </Form.Select>
              results
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => handleNavigationPageChange('prev')}
                  disabled={pagination.pageIndex === 0}
                />
                {pages.map((page, index) => {
                  if (page === '...') {
                    return <Pagination.Ellipsis key={index} />
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => setPagination({...pagination, pageIndex: page - 1})}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                <Pagination.Next
                  onClick={() => handleNavigationPageChange('next')}
                  disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      )}
    </div>
  )
}
  
